import React, { createContext, useContext, useState, ReactNode } from 'react';
import sandboxesService from '../services/sandboxesService';
import projectsService from '../services/projectsService';

interface CopiedSandbox {
  id: number;
  name: string;
  file_path: string;
  software: string;
  project_id: number;
  revision_id: number;
  history_id: number;
}

interface CopyContextType {
  copiedSandbox: CopiedSandbox | null;
  isDragging: boolean;
  copySandbox: (sandbox: CopiedSandbox) => void;
  pasteSandbox: (destinationProjectId: number, destinationRevisionId: number, newName?: string) => Promise<boolean>;
  clearCopiedSandbox: () => void;
  setIsDragging: (dragging: boolean) => void;
}

const CopyContext = createContext<CopyContextType | undefined>(undefined);

interface CopyProviderProps {
  children: ReactNode;
}

export const CopyProvider: React.FC<CopyProviderProps> = ({ children }) => {
  const [copiedSandbox, setCopiedSandbox] = useState<CopiedSandbox | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const copySandbox = (sandbox: CopiedSandbox) => {
    setCopiedSandbox(sandbox);
    // Also copy to clipboard for external paste operations
    const copyData = {
      type: 'sandbox',
      data: sandbox
    };
    navigator.clipboard.writeText(JSON.stringify(copyData)).catch(console.error);
  };

  const pasteSandbox = async (destinationProjectId: number, destinationRevisionId: number, newName?: string): Promise<boolean> => {
    if (!copiedSandbox) return false;

    try {
      // Get the destination history ID from the revision ID using the service
      const revisionData = await projectsService.getRevisionDetails(destinationProjectId, destinationRevisionId);
      const destinationHistoryId = revisionData.revision.id;

      // Generate new model name if not provided
      const finalNewName = newName || `${copiedSandbox.name} (copy)`;

      // Use the existing sandboxesService to copy the sandbox
      await sandboxesService.copySandbox(
        copiedSandbox.id,
        destinationHistoryId,
        finalNewName
      );

      // Clear copied sandbox after successful paste
      setCopiedSandbox(null);
      return true;
    } catch (error) {
      console.error('Paste operation failed:', error);
      return false;
    }
  };

  const clearCopiedSandbox = () => {
    setCopiedSandbox(null);
  };

  const value: CopyContextType = {
    copiedSandbox,
    isDragging,
    copySandbox,
    pasteSandbox,
    clearCopiedSandbox,
    setIsDragging
  };

  return (
    <CopyContext.Provider value={value}>
      {children}
    </CopyContext.Provider>
  );
};

export const useCopy = (): CopyContextType => {
  const context = useContext(CopyContext);
  if (context === undefined) {
    throw new Error('useCopy must be used within a CopyProvider');
  }
  return context;
};